import React from "react";
import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Avatar, Box, Stack, Typography, useMediaQuery } from "@mui/material";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import PageRoutes from "../../../../utils/constants";
import momentTimezone from "moment-timezone";

const userTimeZone = momentTimezone.tz.guess();

function toTitleCase(str?: string): string {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px 8px", // Clean spacing like the photo
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: "pointer",
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

function ScheduleTable({ data, setFilter }: any) {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("567"));
  const navigate = useNavigate();
  
  const handleNavigate = (route: any) => {
    navigate(route);
  };

  const renderImage = (game: any) => {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", flexGrow: 1 }}>
        <Box
          component={"img"}
          title="Image"
          alt={game?.game_id}
          sx={{
            objectFit: "contain",
            width: 1,
            height: "46px",
            borderRadius: 2,
          }}
          src={game?.card_image_url_wide}
        />
      </Box>
    );
  };

  return (
    <div>
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Table size="small" aria-label="customized table">
          <TableBody>
            {data?.length > 0 && data ? (
              data.map((row: any, index: number) => (
                <StyledTableRow
                  onClick={() =>
                    handleNavigate(
                      PageRoutes.Watch.replace(":gameId", row?.game_id)
                    )
                  }
                  key={row.game_id || index}
                >
                  <StyledTableCell
                    align="left"
                    sx={{
                      pr: 0,
                      width: "120px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: { xs: "11px", sm: "12px" },
                        color: "text.primary",
                        textTransform: "none",
                      }}
                    >
                      {moment(row.game_date).format("MMM. DD")}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: "normal",
                        fontWeight: 400,
                        fontSize: { xs: "11px", sm: "12px" },
                        color: "text.primary",
                        textTransform: "none",
                      }}
                    >
                      {momentTimezone
                        .tz(
                          row.game_date_time,
                          "YYYY-MM-DD hh:mm:ss.SSSSSS a Z",
                          "America/New_York"
                        )
                        .tz(userTimeZone)
                        .format("h:mm A z")}
                    </Typography>
                  </StyledTableCell>
                  
                  {!!row?.card_image_url_wide && (
                    <StyledTableCell
                      align="left"
                      colSpan={2}
                      sx={{ pr: 0, width: "150px" }}
                    >
                      <Stack direction="row" spacing={1} alignItems={"center"}>
                        {renderImage(row)}
                      </Stack>
                    </StyledTableCell>
                  )}

                  {!row?.card_image_url_wide && (
                    <StyledTableCell
                      align="left"
                      sx={{ pr: 0, width: "150px" }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems={"center"}>
                        <Avatar src={row?.away_team_logo} sx={{ width: 36, height: 36 }} />
                        <Stack>
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: { xs: "11px", sm: "12px" },
                              whiteSpace: "nowrap",
                              color: "text.primary",
                              textTransform: "none",
                            }}
                          >
                            {toTitleCase(row?.away_team)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: { xs: "11px", sm: "12px" },
                              whiteSpace: "nowrap",
                              color: "text.primary",
                              textTransform: "none",
                            }}
                          >
                            {toTitleCase(row?.away_team_mascot)}
                          </Typography>
                        </Stack>
                      </Stack>

                      {isMobile && (
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems={"center"}
                          sx={{ mt: 1 }}
                        >
                          <Avatar src={row?.home_team_logo} sx={{ width: 36, height: 36 }} />
                          <Stack>
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "normal",
                                fontWeight: 400,
                                fontSize: { xs: "11px", sm: "12px" },
                                whiteSpace: "nowrap",
                                color: "text.primary",
                                textTransform: "none",
                              }}
                            >
                              {toTitleCase(row?.home_team)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "nowrap",
                                fontSize: { xs: "11px", sm: "12px" },
                                fontStyle: "normal",
                                fontWeight: 400,
                                color: "text.primary",
                                textTransform: "none",
                              }}
                            >
                              {toTitleCase(row?.home_team_mascot)}
                            </Typography>
                          </Stack>
                        </Stack>
                      )}
                    </StyledTableCell>
                  )}

                  {!isMobile && !row?.card_image_url_wide && (
                    <StyledTableCell
                      sx={{ pr: 0, width: "150px" }}
                      align="left"
                    >
                      <Stack direction="row" spacing={1.5} alignItems={"center"}>
                        <Avatar src={row?.home_team_logo} sx={{ width: 36, height: 36 }} />
                        <Stack>
                          <Typography
                            variant="caption"
                            sx={{
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: { xs: "11px", sm: "12px" },
                              whiteSpace: "nowrap",
                              color: "text.primary",
                              textTransform: "none",
                            }}
                          >
                            {toTitleCase(row?.home_team)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              whiteSpace: "nowrap",
                              fontStyle: "normal",
                              fontWeight: 400,
                              fontSize: { xs: "11px", sm: "12px" },
                              color: "text.primary",
                              textTransform: "none",
                            }}
                          >
                            {toTitleCase(row?.home_team_mascot)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </StyledTableCell>
                  )}

                  <StyledTableCell sx={{ pr: 1 }} align="left">
                    <Box>
                      <Typography
                        variant="body2"
                        maxWidth={{ xs: "auto", sm: "130px" }}
                        sx={{
                          fontStyle: "normal",
                          fontWeight: 400,
                          fontSize: { xs: "11px", sm: "12px" },
                          color: "text.secondary",
                          textTransform: "none",
                        }}
                      >
                        {toTitleCase(row?.facility_name)}
                      </Typography>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontStyle: "normal",
                    }}
                  >
                    No games found related to search
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default ScheduleTable;
